import { getMimeTypeFromExtension, parseDataUri } from "@packages/commons";

/**
 * True if the string contains only base64-safe characters (so atob won't throw InvalidCharacterError).
 * atob requires Latin1; base64 uses A-Za-z0-9+/= so we only decode when we're sure it's not plain text.
 */
const isBase64Safe = (s: string): boolean => {
  if (!s || typeof s !== "string") {
    return false;
  }
  return /^[A-Za-z0-9+/]*=*$/.test(s);
};

/**
 * Downloads a file in the browser
 * @param data - Data URL (data:...;base64,...), plain base64 string, or plain text
 * @param filename - Name of the file to download
 * @param mimeType - Optional MIME type (will be detected from filename if not provided)
 */
export const downloadFileInBrowser = (
  data: string,
  filename: string,
  mimeType?: string
): void => {
  try {
    const detectedMimeType = mimeType || getMimeTypeFromExtension(filename);
    let blob: Blob;

    const parsed = parseDataUri(data);
    if (parsed) {
      try {
        const binaryString = atob(parsed.base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: parsed.mimeType || detectedMimeType });
      } catch {
        blob = new Blob([parsed.base64Data], { type: parsed.mimeType || detectedMimeType });
      }
    } else if (isBase64Safe(data)) {
      try {
        const binaryString = atob(data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: detectedMimeType });
      } catch {
        blob = new Blob([data], { type: detectedMimeType });
      }
    } else {
      blob = new Blob([data], { type: detectedMimeType });
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading file:", error);
    throw new Error(`Failed to download file ${filename}`);
  }
};
